import React, { useState, useEffect } from 'react';
import {
  TableHead, TableRow, TableCell, IconButton, Menu, MenuItem,
  ListItemIcon, Checkbox, ListItemText
} from '@mui/material';
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined';
import { makeStyles } from '@material-ui/core/styles';
import TableSortLabel from '@mui/material/TableSortLabel';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { formatColumnValue } from '../../../../hooks/Formatter';

const useStyles = makeStyles({
  table: {
    minWidth: 800,
    borderCollapse: "separate !important",
    borderSpacing: "0 16px !important",
    border: "none !important"
  },
  tableRow: {
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 16px 0px rgba(0, 0, 0, 0.10)"
  },
  tableHead: {
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 2px 16px 0px rgba(0, 0, 0, 0.10)",
    borderSpacing: "0 !important",
  },
  tableCell: {
    padding: "5px 20px !important"
  },
  menu: {
    '& .MuiButtonBase-root ': {
      borderRadius: "4px",
      background: "#F5F7F4",
    },
    '& .MuiList-padding ': {
      padding: "0px !important",
    }
  },
  menuItem: {
    '& .MuiTypography-root ': {
      fontSize: "14px !important",
      fontWeight: "600 !important",
      color: "#59595A !important",
      fontFamily: 'Poppins, sans-serif',
    }
  }
});

const EditTableHeader = (props) => {
  const classes = useStyles();
  const { anchorEl, handleOpen, handleClose, selectedMenuIndex, rows, headCells, handleCheckboxSelect, handleAllCheckboxSelect, onRequestSort, sortOrder } = props;

  const getUniqueColumnValues = (rows, column) => {
    const uniqueValues = new Set(rows.map(item => item[column]));
    return Array.from(uniqueValues);
  };

  const [filterOptions, setFilterOptions] = useState({});
  const [checkedOptions, setCheckedOptions] = useState({});
  const [isAllSelected, setIsAllSelected] = useState({});
  const [visibleRows, setVisibleRows] = useState([]);

  const createSortHandler = (property) => () => {
    onRequestSort(property);
  };

  useEffect(() => {
    const options = {};
    const initialCheckedOptions = {};
    const initialIsAllSelected = {};
    setVisibleRows(rows);
    headCells.forEach(cell => {
      const uniqueValues = getUniqueColumnValues(visibleRows, cell.id);
      options[cell.id] = uniqueValues;
      initialCheckedOptions[cell.id] = uniqueValues;
      initialIsAllSelected[cell.id] = true;
    });
    setFilterOptions(options);
    setCheckedOptions(initialCheckedOptions);
    setIsAllSelected(initialIsAllSelected);
  }, [visibleRows, headCells]);



  const handleSelectOption = (event, column) => {
    const value = event.target.value;
    const isChecked = event.target.checked;
    setCheckedOptions((prev) => {
      let newCheckedOptions = { ...prev };
      if (!newCheckedOptions[column]) {
        newCheckedOptions[column] = [];
      }
      if (isChecked) {
        newCheckedOptions[column].push(value);
      } else {
        newCheckedOptions[column] = newCheckedOptions[column].filter(opt => opt !== value);
      }
      handleCheckboxSelect(newCheckedOptions, column, isChecked);
      return newCheckedOptions;
    });

  };

  const handleAllSelectOption = (event, column) => {
    const isChecked = event.target.checked;
    setIsAllSelected((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
    setCheckedOptions((prev) => ({
      ...prev,
      [column]: isAllSelected[column] ? [] : filterOptions[column],
    }));
    handleAllCheckboxSelect(isChecked);
  };
  return (
    <TableHead className={classes.tableHead}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            className={classes.tableCell}
            style={{ color: "#929497", fontSize: "14px" }}
          >
            <TableSortLabel
              hideSortIcon={true}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {headCell.id !== 'Threshold' && headCell.id !== 'Units' && (
                <>
                  <IconButton
                    aria-controls={`${headCell.id}-menu`}
                    aria-haspopup="true"
                    onClick={(event) => handleOpen(event, headCell.id)}
                    color="inherit"
                  >
                    <ArrowDropDownOutlinedIcon />
                  </IconButton>
                  <Menu
                    id={`${headCell.id}-menu`}
                    anchorEl={anchorEl}
                    open={selectedMenuIndex === headCell.id && Boolean(anchorEl)}
                    onClose={handleClose}
                    classes={{ paper: classes.menu }}
                  >
                    <MenuItem value="all">
                      <ListItemIcon>
                        <Checkbox
                          checked={isAllSelected[headCell.id] || false}
                          value="all"
                          indeterminate={
                            checkedOptions[headCell.id]?.length > 0 &&
                            checkedOptions[headCell.id]?.length < filterOptions[headCell.id]?.length
                          }
                          onChange={(e) => handleAllSelectOption(e, headCell.id)}
                          sx={{
                            color: '#2B5692',
                            '&.Mui-checked': {
                              color: '#2B5692',
                            },
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="All" className={classes.menuItem} />
                    </MenuItem>
                    {filterOptions[headCell.id]?.map((option, index) => {
                      const formateText = option !== null && option !== undefined ? formatColumnValue(option) : 'N/A';  // Handle null or undefined options
                      return (
                        <MenuItem key={index} value={option}>
                          <ListItemIcon>
                            <Checkbox
                              value={option}
                              checked={checkedOptions[headCell.id]?.includes(option) || false}
                              onChange={(e) => handleSelectOption(e, headCell.id)}
                              sx={{
                                color: '#2B5692',
                                '&.Mui-checked': {
                                  color: '#2B5692',
                                },
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary={formateText} className={classes.menuItem} />
                        </MenuItem>
                      );
                    })}
                  </Menu>
                </>
              )}
              {(headCell.id === 'Threshold') && (
                sortOrder === 'desc' ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />
              )}</TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

export default EditTableHeader;
